import type { ReactElement } from "react";
import React, { useState, useEffect } from "react";
import type { RouterProps } from "react-router-dom";
import { Router, Routes, Route } from "react-router-dom";

import { Layout, Drawer, ConfigProvider, theme } from "antd";

import type { User } from "@chirpstack/chirpstack-api-grpc-web/api/user_pb";

import Header from "./components/Header";
import Menu from "./components/Menu";

// dashboard
import Dashboard from "./views/dashboard/Dashboard";
import DevStatDashboard from "./views/dashboard/DevStatDashboard";

// mqtt devices
import MqttDevices from "./views/mqtt-devices/MqttDevices";
import MqttDeviceDetails from "./views/mqtt-devices/MqttDeviceDetails";

// users
import Login from "./views/users/Login";
import ListUsers from "./views/users/ListUsers";
import CreateUser from "./views/users/CreateUser";
import EditUser from "./views/users/EditUser";
import ChangeUserPassword from "./views/users/ChangeUserPassword";

// tenants
import TenantRedirect from "./views/tenants/TenantRedirect";
import ListTenants from "./views/tenants/ListTenants";
import CreateTenant from "./views/tenants/CreateTenant";
import TenantLoader from "./views/tenants/TenantLoader";

// api keys
import ListAdminApiKeys from "./views/api-keys/ListAdminApiKeys";
import CreateAdminApiKey from "./views/api-keys/CreateAdminApiKey";

// device-profile templates
import ListDeviceProfileTemplates from "./views/device-profile-templates/ListDeviceProfileTemplates";
import CreateDeviceProfileTemplate from "./views/device-profile-templates/CreateDeviceProfileTemplate";
import EditDeviceProfileTemplate from "./views/device-profile-templates/EditDeviceProfileTemplate";

// regions
import ListRegions from "./views/regions/ListRegions";
import RegionDetails from "./views/regions/RegionDetails";

// stores
import SessionStore from "./stores/SessionStore";
import ThemeStore from "./stores/ThemeStore";

import history from "./history";

interface IProps extends Omit<RouterProps, "location" | "navigationType" | "navigator"> {
  history: typeof history;
  children: (ReactElement | undefined)[];
}

const CustomRouter = ({ history, ...props }: IProps) => {
  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });

  React.useLayoutEffect(() => history.listen(setState), [history]);

  return <Router {...props} location={state.location} navigationType={state.action} navigator={history} />;
};

function App() {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(ThemeStore.getIsDarkMode());
  
  useEffect(() => {
    // Initialize user state after SessionStore has had time to fetch profile
    const initializeUser = () => {
      setUser(SessionStore.getUser());
    };
    
    // Initialize theme state
    const initializeTheme = () => {
      setIsDarkMode(ThemeStore.getIsDarkMode());
    };
    
    // Set initial states
    initializeUser();
    initializeTheme();
    
    // Listen for session changes
    SessionStore.on("change", initializeUser);
    
    // Listen for theme changes
    ThemeStore.on("change", initializeTheme);
    
    // Set up page visibility handling for mobile browsers
    const handleVisibilityChange = () => {
      if (!document.hidden && SessionStore.getToken()) {
        // Page became visible, attempt session recovery first, then refresh
        SessionStore.attemptSessionRecovery();
        SessionStore.refreshSession();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Attempt session recovery on app load
    setTimeout(() => {
      SessionStore.attemptSessionRecovery();
    }, 1000); // Wait 1 second after app loads
    
    // Cleanup listeners on unmount
    return () => {
      SessionStore.removeListener("change", initializeUser);
      ThemeStore.removeListener("change", initializeTheme);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: isDarkMode ? "#1890ff" : "#1890ff",
          colorBgBase: isDarkMode ? "#141414" : "#ffffff",
          colorBgContainer: isDarkMode ? "#1f1f1f" : "#ffffff",
          colorBgElevated: isDarkMode ? "#262626" : "#ffffff",
          colorBorder: isDarkMode ? "#424242" : "#d9d9d9",
          colorText: isDarkMode ? "#ffffff" : "#000000",
          colorTextSecondary: isDarkMode ? "#a6a6a6" : "#666666",
        },
      }}
    >
      <Layout style={{ minHeight: "100vh" }}>
        <CustomRouter history={history}>
        <Routes>
          <Route path="/" element={<TenantRedirect />} />
          <Route path="/login" element={<Login />} />
        </Routes>

        {user && (
          <div>
            <Layout.Header className="layout-header">
              <Header user={user} onMobileMenuToggle={handleMobileMenuToggle} />
            </Layout.Header>
            <Layout className="layout">
              <Layout.Sider 
                width="300" 
                theme={isDarkMode ? "dark" : "light"} 
                className="layout-menu"
              >
                <Menu />
              </Layout.Sider>
              <Layout.Content className="layout-content" style={{ padding: "24px 24px 24px" }}>
                <Routes>
                  <Route path="/devstat-dashboard" element={<DevStatDashboard />} />
                  <Route path="/mqtt-devices" element={<MqttDevices />} />
                  <Route path="/mqtt-devices/:serial" element={<MqttDeviceDetails />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/tenants" element={<ListTenants />} />
                  <Route path="/tenants/create" element={<CreateTenant />} />
                  <Route path="/tenants/:tenantId/*" element={<TenantLoader />} />

                  <Route path="/users" element={<ListUsers />} />
                  <Route path="/users/create" element={<CreateUser />} />
                  <Route path="/users/:userId" element={<EditUser />} />
                  <Route path="/users/:userId/password" element={<ChangeUserPassword />} />

                  <Route path="/api-keys" element={<ListAdminApiKeys />} />
                  <Route path="/api-keys/create" element={<CreateAdminApiKey />} />

                  <Route path="/device-profile-templates" element={<ListDeviceProfileTemplates />} />
                  <Route path="/device-profile-templates/create" element={<CreateDeviceProfileTemplate />} />
                  <Route
                    path="/device-profile-templates/:deviceProfileTemplateId/edit"
                    element={<EditDeviceProfileTemplate />}
                  />

                  <Route path="/regions" element={<ListRegions />} />
                  <Route path="/regions/:id" element={<RegionDetails />} />
                </Routes>
              </Layout.Content>
            </Layout>
            
            {/* Mobile Menu Drawer */}
            <Drawer
              title="Menu"
              placement="left"
              onClose={handleMobileMenuClose}
              open={mobileMenuOpen}
              className="mobile-menu-drawer"
              width={280}
              bodyStyle={{ padding: 0 }}
            >
              <Menu isMobile={true} onMobileMenuClose={handleMobileMenuClose} />
            </Drawer>
          </div>
        )}
        </CustomRouter>
      </Layout>
    </ConfigProvider>
  );
}

export default App;
